'use client';

import { useTransition } from 'react';
import { setPowerModeAction } from '@/src/server/actions/set-active-context.action';
import type { NavigationContext } from '@/src/lib/navigation';

export interface PowerModeToggleProps {
  activeContext: NavigationContext;
  enabled: boolean;
}

/**
 * Power-Mode-Toggle – nur sichtbar für SUPER_ADMIN.
 *
 * Wenn aktiv, werden in der Sidebar ALLE Items unabhängig von
 * Berechtigungen und Feature-Flags gezeigt.
 */
export function PowerModeToggle({ activeContext, enabled }: PowerModeToggleProps) {
  const [isPending, startTransition] = useTransition();

  function handleToggle() {
    const next = !enabled;
    startTransition(() => {
      const fd = new FormData();
      fd.set('enabled', next ? 'true' : 'false');
      fd.set('context', activeContext);
      void setPowerModeAction(fd);
    });
  }

  return (
    <button
      type="button"
      onClick={handleToggle}
      disabled={isPending}
      aria-pressed={enabled}
      aria-label={enabled ? 'Power-Mode deaktivieren' : 'Power-Mode aktivieren'}
      data-tour="power-mode-toggle"
      title={
        enabled
          ? 'Power-Mode aktiv – alle Bereiche sichtbar'
          : 'Power-Mode – alle Bereiche anzeigen (nur Sysadmin)'
      }
      className={[
        'inline-flex items-center gap-2 rounded-[var(--k-radius-md)] border px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] transition duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--k-color-focus-ring)] disabled:cursor-wait disabled:opacity-60',
        enabled
          ? 'border-[var(--k-color-danger)]/40 bg-[var(--k-color-area-system-soft)] text-[var(--k-color-danger-strong)] shadow-[var(--k-shadow-xs)]'
          : 'border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface)] text-[var(--k-color-text-muted)] hover:border-[var(--k-color-border-default)] hover:bg-[var(--k-color-surface-muted)] hover:text-[var(--k-color-text-primary)]',
      ].join(' ')}
    >
      <span
        aria-hidden="true"
        className={[
          'h-2 w-2 rounded-full',
          enabled ? 'bg-[var(--k-color-danger-action)]' : 'bg-[var(--k-color-text-faint)]',
        ].join(' ')}
      />
      Power-Mode
      <span
        aria-hidden="true"
        className={[
          'ml-1 inline-flex h-4 w-7 items-center rounded-full p-0.5 transition duration-150',
          enabled ? 'bg-[var(--k-color-danger-soft)]' : 'bg-[var(--k-color-border-default)]',
        ].join(' ')}
      >
        <span
          className={[
            'h-3 w-3 rounded-full bg-[var(--k-color-surface)] shadow-sm transition duration-150',
            enabled ? 'translate-x-3' : 'translate-x-0',
          ].join(' ')}
        />
      </span>
    </button>
  );
}
